import { CheckCircle2, ChevronDown, CircleAlert, RefreshCw } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'

type FileLevel = 'spu' | 'sku'
type StatusKind = 'ready' | 'checking' | 'late' | 'missing' | 'loading'

interface JdDailyFile {
  fileName: string
  fileBytes: number
  modifiedAt: string
  sha256: string
  level: FileLevel | null
  queryStart: string | null
  queryEnd: string | null
  error: string | null
  windowMatches: boolean
  ingested: boolean
  ingestedRunId: string | null
  ingestedAt: string | null
  state: 'invalid' | 'wrong_window' | 'ingested' | 'ready'
}

interface AdvertisingSource {
  available: boolean
  amount: number | null
  status: 'settled' | 'provisional' | 'missing' | string
  pageId: string | null
  attribution: string | null
  collectedAt: string | null
}

interface JdDataUpdatePayload {
  generatedAt: string
  today: string
  targetDate: string
  expectedStart: string
  reviewDates: string[]
  catchUpDates: string[]
  collectionDates: string[]
  folder: { relativePath: string; exists: boolean }
  files: JdDailyFile[]
  fileSummary: {
    recognizedCount: number
    validPair: boolean
    pairIngested: boolean
    duplicateLevels: FileLevel[]
  }
  sales: {
    current: boolean
    currentMaxDate: string | null
    runId: string | null
    collectedAt: string | null
    productCounts: { spu?: number; sku?: number } | null
  }
  advertising: {
    targetComplete: boolean
    windowComplete: boolean
    windowStart: string
    windowEnd: string
    sources: {
      regularJzt: AdvertisingSource
      brandContract: AdvertisingSource
      jdAlliance: AdvertisingSource
    }
  }
  complete: boolean
}

interface FileRowState {
  kind: StatusKind
  label: string
  detail: string
}

const exportTimeText = (value?: string | null) => value ? value.replace('T', ' ').slice(0, 19) : '—'
const money = (value: number | null) => value === null ? '—' : `¥${value.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
const fileSize = (value: number) => value >= 1024 * 1024 ? `${(value / 1024 / 1024).toFixed(1)} MB` : `${Math.max(Math.round(value / 1024), 1)} KB`

function StatusPill({ kind, label }: { kind: StatusKind; label: string }) {
  return <span className={`pdd-update-status is-${kind}`}>{label}</span>
}

function stateForFile(file: JdDailyFile | undefined, duplicate: boolean, loading: boolean): FileRowState {
  if (loading && !file) return { kind: 'loading', label: '核对中', detail: '正在读取当天文件夹' }
  if (duplicate) return { kind: 'late', label: '文件重复', detail: '同一层级发现多份文件，请保留唯一原始报表' }
  if (!file) return { kind: 'missing', label: '待放入', detail: '当天文件夹尚未发现该报表' }
  if (file.error) return { kind: 'late', label: '无法识别', detail: file.error }
  if (!file.windowMatches) return { kind: 'late', label: '日期不符', detail: `${file.queryStart || '—'} — ${file.queryEnd || '—'}` }
  if (file.ingested) return { kind: 'ready', label: '已入库', detail: `运行 ${file.ingestedRunId || '—'}` }
  return { kind: 'checking', label: '已发现', detail: '等待 Codex 校验成交金额并入库' }
}

function FileTaskRow({
  index,
  level,
  file,
  duplicate,
  payload,
  loading,
}: {
  index: number
  level: FileLevel
  file: JdDailyFile | undefined
  duplicate: boolean
  payload: JdDataUpdatePayload | null
  loading: boolean
}) {
  const state = stateForFile(file, duplicate, loading)
  const label = level.toUpperCase()
  return (
    <div className="pdd-update-task-row jd-update-task-row">
      <span className={`pdd-update-task-index is-${state.kind}`}>
        {state.kind === 'ready' ? <CheckCircle2 size={17} aria-hidden /> : index}
      </span>
      <div className="pdd-update-task-main">
        <div className="pdd-update-task-title"><strong>{label} 商品明细</strong><span>1份 XLSX</span></div>
        <p>{file ? `${file.fileName} · ${fileSize(file.fileBytes)}` : `${label} 页签 → 下载；保留后台原始文件名`}</p>
      </div>
      <div className="pdd-update-task-range">
        <span>{file ? state.detail : '本次范围'}</span>
        <strong>{payload ? `${payload.expectedStart} — ${payload.targetDate}` : '—'}</strong>
      </div>
      <StatusPill kind={state.kind} label={state.label} />
    </div>
  )
}

function advertisingState(source: AdvertisingSource): { kind: StatusKind; label: string } {
  if (!source.available) return { kind: 'missing', label: '待核对' }
  if (source.status === 'settled') return { kind: 'ready', label: '已结算' }
  return { kind: 'checking', label: '暂估' }
}

function FlowStep({ index, title, detail, complete }: { index: number; title: string; detail: string; complete: boolean }) {
  return (
    <div className={`jd-update-flow-step${complete ? ' is-complete' : ''}`}>
      <span>{complete ? <CheckCircle2 size={16} aria-hidden /> : index}</span>
      <div><strong>{title}</strong><small>{detail}</small></div>
      <em>{complete ? '已完成' : '待开始'}</em>
    </div>
  )
}

function OrderAndAfterSalesUploadReminders({ payload }: { payload: JdDataUpdatePayload | null }) {
  const dateRange = payload ? `${payload.expectedStart} — ${payload.targetDate}` : '正在读取目标日期…'
  const orderFolder = payload?.folder.relativePath || '京东店铺后台每天下载的数据报表/当天日期/'
  return (
    <>
      <div className="jd-after-sales-upload-reminder">
        <CircleAlert size={17} aria-hidden />
        <div>
          <div className="pdd-update-task-title"><strong>请上传订单明细</strong><span>用于净成交参考</span></div>
          <p>按下单时间导出，需完整覆盖 <strong>{dateRange}</strong>。文件放入 <code>{orderFolder}</code>；若文件加密，打开密码仅当次单独提供、不保存。</p>
        </div>
      </div>
      <div className="jd-after-sales-upload-reminder">
        <CircleAlert size={17} aria-hidden />
        <div>
          <div className="pdd-update-task-title"><strong>请上传售后服务单</strong><span>用于扣售后利润</span></div>
          <p>按售后申请日期导出，需完整覆盖 <strong>{dateRange}</strong>。文件放入 <code>京东店铺后台每天下载的数据报表/售后导出数据/</code>；缺少任一天时，店铺总览的“扣售后利润”显示“—”，不会按 0 估算。</p>
        </div>
      </div>
    </>
  )
}

export function JdDataUpdatePage() {
  const [payload, setPayload] = useState<JdDataUpdatePayload | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const loadStatus = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const response = await fetch('/api/jd/data-update-status', { cache: 'no-store' })
      const body = await response.json() as JdDataUpdatePayload & { error?: string }
      if (!response.ok) throw new Error(body.error || `数据状态加载失败：${response.status}`)
      setPayload(body)
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : String(reason))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void loadStatus() }, [loadStatus])

  const filesByLevel = useMemo(() => ({
    spu: payload?.files.filter((file) => file.level === 'spu') || [],
    sku: payload?.files.filter((file) => file.level === 'sku') || [],
  }), [payload])
  const missingCount = (['spu', 'sku'] as FileLevel[]).filter((level) => filesByLevel[level].length === 0).length
  const invalidCount = payload?.files.filter((file) => file.error || !file.windowMatches).length || 0
  const heroTitle = loading && !payload
    ? '正在核对今天的数据'
    : payload?.sales.current
      ? '今天的销售数据已经更新'
      : invalidCount > 0 || (payload?.fileSummary.duplicateLevels.length || 0) > 0
        ? '当天文件需要检查'
        : payload?.fileSummary.validPair
          ? '2份销售报表已发现'
          : `今天还差 ${missingCount} 份销售报表`

  const adRows = payload ? [
    { index: 1, title: '常规京准通', note: '财务记录 · 现金 / 红包 / 返佣返货金，排除联合活动扣费', source: payload.advertising.sources.regularJzt },
    { index: 2, title: '品牌合约', note: payload.advertising.sources.brandContract.attribution || '固定日成本或搜索品专分日报告复算，以来源证据为准', source: payload.advertising.sources.brandContract },
    { index: 3, title: '京东联盟', note: '结算信息按费用日期汇总；未结算时保留预估状态', source: payload.advertising.sources.jdAlliance },
  ] : []
  const salesStepsComplete = Boolean(payload?.sales.current)
  const completedSteps = payload ? [payload.fileSummary.validPair, payload.fileSummary.validPair, salesStepsComplete, salesStepsComplete, payload.complete].filter(Boolean).length : 0
  const advertisingWindowComplete = Boolean(payload?.advertising.targetComplete && payload.advertising.windowComplete)
  const catchUpText = !payload
    ? '正在扫描广告缺口；'
    : payload.catchUpDates.length
      ? `先补 ${payload.catchUpDates.join('、')}；`
      : '最近30天无广告缺口；'

  return (
    <div className="page-stack jd-update-page">
      <div className="page-title-row">
        <div><span className="pdd-platform-label">JD.COM</span><h1>数据更新</h1><p>每天把商智导出的两份销售报表放进当天文件夹，Codex 自动识别 SPU 与 SKU，并完成校验与入库。</p></div>
        <button type="button" className="text-link upload-refresh" onClick={() => void loadStatus()} disabled={loading}><RefreshCw size={14} /> 刷新状态</button>
      </div>

      {error && <div className="form-error">{error}<button type="button" className="text-link" onClick={() => void loadStatus()}>重试</button></div>}

      <section className="overview-section pdd-update-hero">
        <div className="pdd-update-summary">
          <div><span className="pdd-update-eyebrow">今日更新 · {payload?.today || '—'}</span><h2>{heroTitle}</h2><p>Codex 读取后先校验文件类型、30天日期窗和 SPU/SKU 成交金额；校验通过才会进入台账。</p></div>
          <div className="pdd-update-summary-metrics">
            <div><span>目标日期</span><strong>{payload?.targetDate || '—'}</strong></div>
            <div><span>销售数据当前截至</span><strong>{payload?.sales.currentMaxDate || '—'}</strong></div>
            <div><span>今日文件状态</span><strong>{payload?.fileSummary.recognizedCount || 0} / 2 份已发现</strong></div>
          </div>
        </div>

        <div className="pdd-update-workspace jd-update-workspace">
          <div className="pdd-update-task-list">
            <div className="pdd-update-block-title"><div><h3>你今天需要准备</h3><p>商智 → 商品 → 商品明细；最近30个完整自然日，选择“分天”</p></div><span>共 2 份 XLSX</span></div>
            <FileTaskRow index={1} level="spu" file={filesByLevel.spu[0]} duplicate={filesByLevel.spu.length > 1} payload={payload} loading={loading} />
            <FileTaskRow index={2} level="sku" file={filesByLevel.sku[0]} duplicate={filesByLevel.sku.length > 1} payload={payload} loading={loading} />
          </div>
        </div>
        <OrderAndAfterSalesUploadReminders payload={payload} />
        <div className="pdd-update-on-demand jd-update-folder-hint"><div><div className="pdd-update-task-title"><strong>文件放置</strong></div><p>{payload?.folder.relativePath || '京东店铺后台每天下载的数据报表 / 当天日期 /'}。当天文件夹可以是 20260820、2026-0820 或 2026-0820-1017。两份文件放在一起并保留原名，放好后告诉 Codex“更新京东数据”。</p></div></div>
      </section>

      <div className="jd-update-lower-grid">
        <section className="overview-section jd-update-panel">
          <div className="pdd-update-block-title jd-update-panel-title"><div><h2>广告数据由 Codex 补采</h2><p>不要求你另外下载文件；{catchUpText}例行复查 {payload?.reviewDates.join('、') || '目标日、3天前和15天前'}</p></div>{payload && <StatusPill kind={advertisingWindowComplete ? 'ready' : 'checking'} label={advertisingWindowComplete ? '近30天已齐' : payload.catchUpDates.length ? `待补${payload.catchUpDates.length}天` : '待后台核对'} />}</div>
          <div className="jd-update-source-list">{adRows.map((row) => {
            const state = advertisingState(row.source)
            return <div className="jd-update-source-row" key={row.title}><span>{row.index}</span><div><strong>{row.title}</strong><small>{row.note}</small></div><div><strong>{money(row.source.amount)}</strong><small>{payload?.targetDate || '—'}</small></div><StatusPill {...state} /></div>
          })}</div>
          <div className="jd-update-warning"><CircleAlert size={15} aria-hidden />周末和假期缺口会在下次更新优先补齐；三类来源未齐时不写成 0，也不计算完整区间广告后利润。</div>
        </section>

        <section className="overview-section jd-update-panel">
          <div className="pdd-update-block-title jd-update-panel-title"><div><h2>Codex 读取后会自动完成</h2><p>只有全部验收通过，页面才显示“今天已更新”</p></div><span>{completedSteps} / 5</span></div>
          <div className="jd-update-flow">
            <FlowStep index={1} title="识别 SPU 与 SKU 文件" detail="读取工作表与表头，不依赖文件名猜测" complete={Boolean(payload?.fileSummary.validPair)} />
            <FlowStep index={2} title="核对统一30天日期窗" detail={payload ? `${payload.expectedStart} 至 ${payload.targetDate}，分天粒度` : '—'} complete={Boolean(payload?.fileSummary.validPair)} />
            <FlowStep index={3} title="核对 SPU / SKU 成交金额" detail="两份报表差异必须不超过 0.01 元" complete={salesStepsComplete} />
            <FlowStep index={4} title="追加入库并刷新网站" detail="保留原文件、SHA256、运行清单和来源元数据" complete={salesStepsComplete} />
            <FlowStep index={5} title="完成健康与数据验收" detail="数据截至目标日、近30天广告无缺口、仓库锁释放、网站健康" complete={Boolean(payload?.complete)} />
          </div>
        </section>
      </div>

      <details className="overview-section pdd-update-details">
        <summary><span>下载路径与口径说明</span><ChevronDown size={16} aria-hidden /></summary>
        <div className="pdd-update-detail-grid">
          <article><h3>SPU 报表</h3><p>商品明细切换SPU页签，选择分天、全部品类、全部渠道，不使用对比时间。</p></article>
          <article><h3>SKU 报表</h3><p>保持完全相同的日期与筛选，只切换SKU页签后下载，系统会自动识别。</p></article>
          <article><h3>日期规则</h3><p>每天目标是昨天，商品窗口是截至昨天最近30个完整自然日；不足或错日会停止入库。</p></article>
          <article><h3>安全规则</h3><p>原文件只追加、不覆盖；同一文件已成功入库时只显示已存在，不会重复追加。</p></article>
        </div>
      </details>
      {payload && <p className="jd-update-generated-at">状态生成于 {exportTimeText(payload.generatedAt)}</p>}
    </div>
  )
}
